/**
 * Plan-timeline normalizer (pad + planner) — bridges the live FLAT-NODE contract (§16e) to the
 * nested `PlanTimelineResponse` shape the Pad Gantt renders.
 *
 * The live `GET /v1/pads/{id}/timeline` returns:
 *   { level, scope, tracks: string[]  (well UWIs),
 *     nodes: [{ track, kind, label, planned_start/end, actual_start/end,
 *               depth_from/to, status, external_id, event_id, detail_ref }] }
 * while `PadGantt` (and the mock) expect:
 *   { level, scope, tracks: [{ id, label, kind, bars: PlanTimelineBar[] }] }
 *
 * This groups the flat nodes by their `track` into one nested track each, turning
 * every dated node into a bar. It also passes a response that is ALREADY nested
 * straight through (mock/standalone), so both wire shapes render identically.
 */
import type { PlanTimelineResponse, PlanTimelineTrack, PlanTimelineBar } from "@api/types";

/** A single node in the flat-node timeline contract (superset of live fields). */
interface FlatTimelineNode {
  track?: string | null;
  external_id?: string | null;
  event_id?: string | null;
  kind?: string | null;
  label?: string | null;
  op_type?: string | null;
  depth_from?: number | null;
  depth_to?: number | null;
  planned_start?: string | null;
  planned_end?: string | null;
  actual_start?: string | null;
  actual_end?: string | null;
  status?: string | null;
  is_npt?: boolean | null;
  is_offline?: boolean | null;
}

/** The raw response, permissive over both the flat-node and nested shapes. */
export interface RawTimeline {
  level?: string;
  scope?: string;
  tracks?: unknown;
  nodes?: FlatTimelineNode[] | null;
}

/** True when a value looks like an already-nested track object (has `bars`). */
function isNestedTrack(t: unknown): t is PlanTimelineTrack {
  return typeof t === "object" && t !== null && "bars" in t;
}

/** A node contributes a bar only if it carries at least one dated endpoint. */
function isDated(n: FlatTimelineNode): boolean {
  return Boolean(n.planned_start || n.planned_end || n.actual_start || n.actual_end);
}

/** min of two ISO date strings (nullable). */
function minIso(a: string | null, b: string | null | undefined): string | null {
  if (!b) return a;
  if (!a) return b;
  return b < a ? b : a;
}
/** max of two ISO date strings (nullable). */
function maxIso(a: string | null, b: string | null | undefined): string | null {
  if (!b) return a;
  if (!a) return b;
  return b > a ? b : a;
}

/**
 * Coalesce a track's granular activity nodes into ONE plan span + ONE actual
 * span (spud→TD). The flat-node feed carries thousands of minute-level ops per
 * well; the pad Gantt wants a single well-level bar per row, not a trace per op
 * (which would render thousands of Plotly traces). Plan/actual are kept separate
 * so the Gantt still draws the planned-vs-actual pair per well.
 */
function coalesceTrackBars(trackKey: string, nodes: FlatTimelineNode[]): PlanTimelineBar[] {
  let pStart: string | null = null,
    pEnd: string | null = null,
    aStart: string | null = null,
    aEnd: string | null = null,
    npt = false,
    offline = false;
  for (const n of nodes) {
    pStart = minIso(pStart, n.planned_start);
    pEnd = maxIso(pEnd, n.planned_end);
    aStart = minIso(aStart, n.actual_start);
    aEnd = maxIso(aEnd, n.actual_end);
    if (n.is_npt || n.status === "npt") npt = true;
    if (n.is_offline || n.status === "offline") offline = true;
  }
  const bars: PlanTimelineBar[] = [];
  if (aStart || aEnd) {
    bars.push({
      id: `${trackKey}-actual`,
      label: "Actual",
      op_type: "actual",
      planned_start: null,
      planned_end: null,
      actual_start: aStart,
      actual_end: aEnd,
      is_npt: npt,
      is_offline: offline,
    });
  }
  if (pStart || pEnd) {
    bars.push({
      id: `${trackKey}-plan`,
      label: "Plan",
      op_type: "plan",
      planned_start: pStart,
      planned_end: pEnd,
      actual_start: null,
      actual_end: null,
      is_npt: false,
      is_offline: false,
    });
  }
  return bars;
}

/**
 * Normalize any pad-timeline response to the nested `PlanTimelineResponse` the
 * Gantt renders. Returns `null` when there is nothing plottable so the caller
 * shows its empty-state. Never throws on a shape mismatch.
 */
export function normalizeTimeline(raw: RawTimeline | undefined): PlanTimelineResponse | null {
  if (!raw || typeof raw !== "object") return null;
  const rawTracks = Array.isArray(raw.tracks) ? raw.tracks : [];

  // Already-nested (mock / legacy) — pass through untouched.
  if (rawTracks.some(isNestedTrack)) {
    const tracks = rawTracks.filter(isNestedTrack);
    return tracks.length ? { level: raw.level ?? "pad", scope: raw.scope ?? "", tracks } : null;
  }

  // Flat-node: group dated `nodes` by `track`, then coalesce each track's ops
  // into one plan + one actual span (one Gantt row per well).
  const nodes = Array.isArray(raw.nodes) ? raw.nodes : [];
  const nodesByTrack = new Map<string, FlatTimelineNode[]>();
  for (const n of nodes) {
    const key = typeof n?.track === "string" ? n.track : "";
    if (!key || !isDated(n)) continue;
    const list = nodesByTrack.get(key);
    if (list) list.push(n);
    else nodesByTrack.set(key, [n]);
  }

  // Preserve the response's track order (string[] of UWIs); include a track only
  // if it has at least one dated bar so the Gantt isn't padded with empty rows.
  const orderedKeys = rawTracks.filter((tk): tk is string => typeof tk === "string");
  const tracks: PlanTimelineTrack[] = [];
  for (const key of orderedKeys) {
    const trackNodes = nodesByTrack.get(key);
    if (!trackNodes?.length) continue;
    const bars = coalesceTrackBars(key, trackNodes);
    if (bars.length) tracks.push({ id: key, label: key, kind: "well", bars });
  }
  if (tracks.length === 0) return null;
  return { level: raw.level ?? "pad", scope: raw.scope ?? "", tracks };
}
